import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import ServicesSection from './components/ServicesSection';
import CalendlySection from './components/CalendlySection';
import CalendlyModal from './components/CalendlyModal';
import FAQSection from './components/FAQSection';
import CMSToolbar from '../../components/cms/CMSToolbar';
import SEOHead from '../../components/seo/SEOHead';
import StructuredData from '../../components/seo/StructuredData';
import { useCMS } from '../../contexts/CMSContext';
import type { ServiceItem } from '../../types/cms';

const CALENDLY_URLS = {
  consulta: 'https://calendly.com/dariorojasnutricion/consulta',
  consulta30: 'https://calendly.com/dariorojasnutricion/30min',
  deportiva: 'https://calendly.com/dariorojasnutricion/nutricion-deportiva',
  plan: 'https://calendly.com/dariorojasnutricion/plan-alimentario-personalizado',
  mensual: 'https://calendly.com/dariorojasnutricion/seguimiento-mensual',
};

export default function Home() {
  const { data } = useCMS();
  const seo = data.seo;
  const homeSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalBusiness',
      name: 'Dario Rojas Nutricion',
      url: 'https://dariorojasnutricion.es/',
      image:
        'https://vhnuplacedgmgryjrjhn.supabase.co/storage/v1/object/public/cms-images/perfil.jpg',
      telephone: '+34 685 546 738',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Palma de Mallorca',
        addressRegion: 'Islas Baleares',
        addressCountry: 'ES',
      },
      openingHours: 'Mo-Fr 09:00-18:00',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Dario Rojas',
      jobTitle: 'Nutricionista y Dietista',
      worksFor: {
        '@type': 'MedicalBusiness',
        name: 'Dario Rojas Nutricion',
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Palma de Mallorca',
        addressCountry: 'ES',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: seo.homeFaqQ1,
          acceptedAnswer: {
            '@type': 'Answer',
            text: seo.homeFaqA1,
          },
        },
        {
          '@type': 'Question',
          name: seo.homeFaqQ2,
          acceptedAnswer: {
            '@type': 'Answer',
            text: seo.homeFaqA2,
          },
        },
        {
          '@type': 'Question',
          name: seo.homeFaqQ3,
          acceptedAnswer: {
            '@type': 'Answer',
            text: seo.homeFaqA3,
          },
        },
      ],
    },
  ];

  const [selectedCalendlyUrl] = useState(CALENDLY_URLS.consulta);
  const [isCalendlyModalOpen, setIsCalendlyModalOpen] = useState(false);
  const [modalCalendlyUrl, setModalCalendlyUrl] = useState(CALENDLY_URLS.consulta);
  const [modalTitle, setModalTitle] = useState('Pedir cita');

  const getServiceCalendlyUrl = (service: ServiceItem): string => {
    let url = CALENDLY_URLS.consulta;
    const normalizedTitle = service.title.toLowerCase();

    if (service.id === 'svc_1' || normalizedTitle.includes('inicial') || normalizedTitle.includes('30')) {
      url = CALENDLY_URLS.consulta30;
    } else if (
      service.id === 'svc_2' ||
      normalizedTitle.includes('plan alimentario') ||
      normalizedTitle.includes('personalizado')
    ) {
      url = CALENDLY_URLS.plan;
    } else if (
      service.id === 'svc_3' ||
      normalizedTitle.includes('seguimiento') ||
      normalizedTitle.includes('mensual')
    ) {
      url = CALENDLY_URLS.mensual;
    } else if (
      service.id === 'svc_4' ||
      normalizedTitle.includes('deportiva') ||
      normalizedTitle.includes('deporte')
    ) {
      url = CALENDLY_URLS.deportiva;
    }

    return url;
  };

  const handleServiceBooking = (service: ServiceItem) => {
    setModalCalendlyUrl(getServiceCalendlyUrl(service));
    setModalTitle(`Pedir cita: ${service.title}`);
    setIsCalendlyModalOpen(true);
  };

  return (
    <div className="font-sans">
      <SEOHead
        title="Dario Rojas | Nutricionista y Dietista en Palma de Mallorca"
        description="Nutricionista y dietista en Palma de Mallorca. Especialista en adelgazar, nutricion deportiva y buena alimentacion con consulta personalizada."
        canonicalPath="/"
        keywords="Dario Rojas, nutricionista Palma de Mallorca, dietista Palma, adelgazar, nutricion deportiva, buena alimentacion"
      />
      <StructuredData data={homeSchema} />
      <Navbar />
      <main>
        <HeroSection />
        <ServicesSection onRequestBooking={handleServiceBooking} />
        <FAQSection />
        <CalendlySection calendlyUrl={selectedCalendlyUrl} />
      </main>
      <CalendlyModal
        isOpen={isCalendlyModalOpen}
        calendlyUrl={modalCalendlyUrl}
        title={modalTitle}
        onClose={() => setIsCalendlyModalOpen(false)}
      />
      <Footer />
      <CMSToolbar />
    </div>
  );
}
